<template>
  <div v-if='pageReady' id='pdf-ready'>
    <ExportCover preTitle='Historical Portfolio Report' :date='new Date()' :title='getExportOrganisation()' />
    <div class='print-page print-portrait x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Filters Applied</h2>
      </ExportPageHeading>
      <ul class='mt-[5mm] text-sm divide-y border-t border-b'>
        <li class='py-1 text-xs'>
          Resolution<br />{{ state.filters.resolution === 'year' ? 'Yearly' : 'Half Yearly' }}
        </li>
        <li class='py-1 text-xs'>
          Years<br />{{ state.filters.year.from }} – {{ state.filters.year.to }}
        </li>
        <li v-if='state.filters.theme?.length' class='py-1 text-xs'>
          Themes <ul><li v-for='theme in state.filters.theme'>{{ theme.name }}</li></ul>
        </li>
        <li v-if='state.filters.objective?.length' class='py-1 text-xs'>
          Objectives <ul><li v-for='objective in state.filters.objective'>{{ objective.name }}</li></ul>
        </li>
        <li v-if='state.filters.assessmentTemplate?.length' class='py-1 text-xs'>
          Templates <ul><li v-for='assessmentTemplate in state.filters.assessmentTemplate'>{{ assessmentTemplate.name }}</li></ul>
        </li>
        <li v-if='state.filters.organisationAsset?.length' class='py-1 text-xs'>
          Assets <ul><li v-for='organisationAsset in state.filters.organisationAsset'>{{ organisationAsset.name }}</li></ul>
        </li>
        <li v-if='state.filters.organisationAssetManager?.length' class='py-1 text-xs'>
          Managers <ul><li v-for='organisationAssetManager in state.filters.organisationAssetManager'>{{ organisationAssetManager.name }}</li></ul>
        </li>
        <li v-if='state.filters.platformAsset?.length' class='py-1 text-xs'>
          Assets <ul><li v-for='platformAsset in state.filters.platformAsset'>{{ platformAsset.name }}</li></ul>
        </li>
        <li v-if='state.filters.platformAssetManager?.length' class='py-1 text-xs'>
          Managers <ul><li v-for='platformAssetManager in state.filters.platformAssetManager'>{{ platformAssetManager.name }}</li></ul>
        </li>
        <li v-if='state.filters.organisation?.length' class='py-1 text-xs'>
          Organisation <ul><li v-for='organisation in state.filters.organisation'>{{ organisation.name }}</li></ul>
        </li>
      </ul>
    </div>
    <div class='print-page print-landscape x-auto-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Results</h2>
        <p>Scores for all engagements.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphHistoricalPortfolioTable
          :params='params'
          :showOrganisation='isSuperAdminView()'
          :showAsset='true'
          :showAssetManager='true'
          :view='view'
          :isPrint='true'
        />
      </div>
    </div>
    <div class='print-page print-landscape x-fixed-y-auto print-padding'>
      <ExportPageHeading preTitle='Historical Portfolio Report' :date='new Date()' :title='getExportOrganisation()'>
        <h2>Theme Trends</h2>
        <p>Theme transformation year on year.</p>
      </ExportPageHeading>
      <div class='mt-8'>
        <GraphHistoricalThemeBar :params='params' :showKey='true' />
      </div>
    </div>
    <ExportDisclaimerPage preTitle='Historical Portfolio Report' :date='new Date()' :title='getExportOrganisation()' />
  </div>
</template>

<script setup>

import { computed, reactive, ref } from 'vue';

import { usePayloadRequest } from '@/kernel/api';
import { getStateFromRoute } from '@/router';
import { rehydrateCollections, getExportTitle, getExportOrganisation } from '@/kernel/report';

import ExportCover from '@/components/export/export-cover.vue';
import ExportPageHeading from '@/components/export/export-page-heading.vue';
import ExportDisclaimerPage from '@/components/export/export-disclaimer-page.vue';

import GraphHistoricalPortfolioTable from '@/components/reports/graphs/graph-historical-portfolio-table.vue';
import GraphHistoricalThemeBar from '@/components/reports/graphs/graph-historical-theme-bar.vue';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

import { MultipleRelationInputAdapter } from '@/kernel/adapters';

const filterCollectionMap = [
  {key: 'theme', collection: 'theme'},
  {key: 'objective', collection: 'objective'},
  {key: 'assessmentTemplate', collection: 'assessment-template'},
  {key: 'organisationAsset', collection: 'organisation-asset'},
  {key: 'organisationAssetManager', collection: 'organisation-asset-manager'},
  {key: 'platformAsset', collection: 'platform-asset'},
  {key: 'platformAssetManager', collection: 'platform-asset-manager'},
  {key: 'organisation', collection: 'organisation'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      theme: preload?.filters?.theme || null,
      objective: preload?.filters?.objective || null,
      assessmentTemplate: preload?.filters?.assessmentTemplate || null,
      organisationAsset: preload?.filters?.organisationAsset || null,
      organisationAssetManager: preload?.filters?.organisationAssetManager || null,
      platformAsset: preload?.filters?.platformAsset || null,
      platformAssetManager: preload?.filters?.platformAssetManager || null,
      organisation: preload?.filters?.organisation || null,
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 3,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      resolution: preload?.filters?.resolution || 'year'
    }
  }
}

const state = reactive(getDefaultState());
const filters = reactive(getDefaultState().filters);

const pageReady = ref(false);
const routeState = getStateFromRoute();

const resolutionInputOptions = [
  {label: 'Year', value: 'year'},
  {label: 'Half Year', value: 'halfYear'}
];

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const params = computed(() => {
  const args = {...state.filters};

  filterCollectionMap.forEach((pair) => {
    args[pair.key] = MultipleRelationInputAdapter(pair.collection)(pair.key, state.filters[pair.key]);
  });

  if (state.filters.objective) {
    args.objectiveCode = state.filters.objective.map(objective => objective.code);
    delete args.objective;
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
});

const loadState = async () => {
  document.title = getExportTitle(`Historical Portfolio Report - ${getExportOrganisation()}`, new Date());

  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
    Object.assign(filters, defaultState.filters);
  }

  pageReady.value = true;
}

loadState();

</script>
